/* Complements the original Honey Balm renderer when another product uses this template. */
(() => {
  const compact = value => (value?.textContent || '').replace(/\s+/g, ' ').trim();
  const isHeading = node => /^H[1-6]$/.test(node?.tagName || '');
  const firstImage = nodes => nodes.map(node => node.querySelector('img')).find(Boolean);
  const paragraphs = nodes => nodes.filter(node => node.tagName === 'P' && compact(node) && !node.querySelector('img')).map(compact);
  const put = (root, selector, value) => {
    const target = root?.querySelector(selector);
    if (target) target.textContent = value || '';
  };
  const setImage = (root, selector, source, fallback) => {
    const target = root?.querySelector(selector);
    const selected = source?.getAttribute('src') ? source : fallback;
    if (!target || !selected?.getAttribute('src')) {
      if (target) target.hidden = true;
      return;
    }
    target.hidden = false;
    target.removeAttribute('srcset');
    target.removeAttribute('sizes');
    target.src = selected.getAttribute('src');
    target.alt = selected.alt || fallback?.alt || '';
  };
  const automatic = root => root && root.dataset.mhpAuto !== 'false';
  const hide = root => { if (root) root.hidden = true; };

  function readCopy(source) {
    const nodes = Array.from(source.content.children).filter(node => compact(node) || node.querySelector('img'));
    const headings = nodes.filter(node => node.tagName === 'H2');
    const h1 = nodes.find(node => node.tagName === 'H1');
    if (!h1 || headings.length < 11) return null;
    // The last seven H2s are the fixed sections. Earlier H2s may include an
    // extra introductory line, but the final four are always benefit cards.
    const sections = headings.slice(-7);
    const benefitHeadings = headings.slice(0, -7).slice(-4);
    if (benefitHeadings.length !== 4) return null;
    const between = (start, end) => nodes.slice(nodes.indexOf(start) + 1, end ? nodes.indexOf(end) : undefined);
    const before = node => nodes[nodes.indexOf(node) - 1];
    const eyebrow = node => {
      const label = before(node)?.tagName === 'P' ? compact(before(node)) : '';
      return label.length <= 55 && !/[.!?]$/.test(label) ? label : '';
    };
    const afterHeading = (heading, boundary) => {
      const segment = between(heading, boundary);
      const next = segment.find(isHeading);
      return paragraphs(next ? segment.slice(0, segment.indexOf(next)) : segment)[0] || '';
    };
    const benefitIntro = paragraphs(between(h1, benefitHeadings[0]))[0] || afterHeading(headings[0], benefitHeadings[0]);
    const benefitCards = benefitHeadings.map((heading, i) => ({
      title: compact(heading),
      body: afterHeading(heading, i < 3 ? benefitHeadings[i + 1] : sections[0])
    }));

    const focusNodes = between(sections[0], sections[1]);
    const focusText = paragraphs(focusNodes);
    const percentages = [];
    for (let i = 0; i + 1 < focusText.length; i += 1) {
      if (/^\d{1,3}\s*%$/.test(focusText[i])) percentages.push({ number: focusText[i], body: focusText[i + 1] });
    }
    const studyItems = focusNodes.flatMap(node => node.tagName === 'UL' || node.tagName === 'OL' ? Array.from(node.querySelectorAll('li')).map(compact) : []);

    const ingredientNodes = between(sections[1], sections[2]);
    const ingredientHeadings = ingredientNodes.filter(node => node.tagName === 'H3');
    const detailIndex = ingredientHeadings.findIndex(node => /^(composi[cç][aã]o|textura|para quais|f[oó]rmula completa|tipos de pele)/i.test(compact(node)));
    const ingredientCount = detailIndex < 0 ? Math.min(3, ingredientHeadings.length) : detailIndex;
    const ingredients = ingredientHeadings.slice(0, ingredientCount).map((heading, i) => {
      const next = ingredientHeadings[i + 1] || sections[2];
      const segment = between(heading, next);
      return {
        title: compact(heading),
        body: paragraphs(segment)[0] || '',
        image: firstImage(between(i ? ingredientHeadings[i - 1] : sections[1], heading)) || firstImage(segment),
        meta: eyebrow(heading)
      };
    });
    const details = ingredientHeadings.slice(ingredientCount).map((heading, i, list) => ({
      title: compact(heading), body: afterHeading(heading, list[i + 1] || sections[2])
    }));
    if (!details.length) {
      const values = paragraphs(between(ingredientHeadings[ingredientCount - 1], sections[2]));
      for (let i = 1; i + 1 < values.length; i += 2) {
        if (values[i].endsWith(':')) details.push({ title: values[i].slice(0, -1), body: values[i + 1] });
      }
    }

    const scienceNodes = between(sections[2], sections[3]);
    const ritualNodes = between(sections[3], sections[4]);
    const ritualHeadings = ritualNodes.filter(node => node.tagName === 'H3');
    let steps = ritualHeadings.map((heading, i) => ({
      title: compact(heading).replace(/^passo\s*\d+\s*[.·:-]?\s*/i, ''),
      body: afterHeading(heading, ritualHeadings[i + 1] || sections[4])
    }));
    if (!steps.length) {
      const lines = paragraphs(ritualNodes);
      steps = lines.flatMap((line, i) => /^\d+[.)]\s*/.test(line) ? [{ title: line.replace(/^\d+[.)]\s*/, ''), body: lines[i + 1] || '' }] : []);
    }
    const testimonialNodes = between(sections[4], sections[5]);
    const testimonialText = paragraphs(testimonialNodes);
    const reviews = [];
    for (let i = 0; i + 3 < testimonialText.length; i += 1) {
      if (/^avalia[cç][aã]o real\s+\d+/i.test(testimonialText[i])) reviews.push({ quote: testimonialText[i + 1], name: testimonialText[i + 2], meta: testimonialText[i + 3] });
    }
    const expectationNodes = between(sections[5], sections[6]);
    const phases = expectationNodes.filter(node => node.tagName === 'H3').map((heading, i, list) => ({
      title: compact(heading), body: afterHeading(heading, list[i + 1] || sections[6])
    }));
    const faqNodes = between(sections[6]);
    const faqHeadings = faqNodes.filter(node => node.tagName === 'H3');
    let faqs = faqHeadings.map((heading, i) => ({
      question: compact(heading), answer: afterHeading(heading, faqHeadings[i + 1])
    })).filter(item => item.question && item.answer);
    if (!faqs.length) {
      const lines = paragraphs(faqNodes);
      faqs = lines.flatMap((line, i) => line.endsWith('?') && lines[i + 1] ? [{ question: line, answer: lines[i + 1] }] : []);
    }
    return {
      title: source.dataset.productTitle || '',
      benefit: { eyebrow: eyebrow(h1), title: compact(h1), intro: benefitIntro, cards: benefitCards, image: firstImage(between(h1, sections[0])) },
      focus: { eyebrow: eyebrow(sections[0]), title: compact(sections[0]), intro: focusText[0] || '', percentages, studyItems, studyLabel: compact(focusNodes.find(node => node.tagName === 'H3')), note: studyItems.length && focusText.length > 1 ? focusText.at(-1) : '', image: firstImage(between(benefitHeadings[3], sections[0])) },
      ingredients: { eyebrow: eyebrow(sections[1]), title: compact(sections[1]), intro: paragraphs(ingredientNodes)[0] || '', items: ingredients, details },
      science: { eyebrow: eyebrow(sections[2]), title: compact(sections[2]), lines: paragraphs(scienceNodes), points: scienceNodes.flatMap(node => node.tagName === 'UL' || node.tagName === 'OL' ? Array.from(node.querySelectorAll('li')).map(compact) : []), image: firstImage(between(ingredientHeadings[ingredientCount - 1], sections[2])) },
      ritual: { eyebrow: eyebrow(sections[3]), title: compact(sections[3]), intro: paragraphs(ritualNodes)[0] || '', steps, image: firstImage(ritualNodes) },
      testimonials: { eyebrow: eyebrow(sections[4]), title: compact(sections[4]), intro: testimonialText[0] || '', reviews, image: firstImage(testimonialNodes) },
      expectation: { title: compact(sections[5]), intro: paragraphs(expectationNodes)[0] || '', phases },
      faq: { eyebrow: eyebrow(sections[6]), title: compact(sections[6]), intro: paragraphs(faqNodes)[0] || '', items: faqs }
    };
  }

  function hydrate() {
    const source = document.querySelector('[data-mhp-copy-source="true"]');
    if (!source || source.dataset.enabled === 'false' || document.documentElement.dataset.mhpCopySynced === 'true') return;
    const product = readCopy(source);
    const mainImage = document.querySelector('#mhp-main-product-image');
    const galleryImage = pattern => {
      const thumb = Array.from(document.querySelectorAll('[data-mhp-thumb]')).find(item => pattern.test(item.dataset.src || ''));
      if (!thumb) return mainImage;
      const image = document.createElement('img');
      image.src = thumb.dataset.src;
      image.alt = thumb.dataset.alt || product?.title || '';
      return image;
    };
    const roots = ['.benefit-story', '.product-focus', '.formula-section', '.science', '.ritual', '.testimonial-feature', '.expectation', '.faq-section'];
    if (!product) {
      roots.forEach(selector => { const root = document.querySelector(selector); if (automatic(root)) hide(root); });
      console.warn('Mellora PDP: descrição sem as seções essenciais; conteúdo fixo de outro produto ocultado.');
      return;
    }
    const hero = document.querySelector('.product-hero');
    if (automatic(hero)) {
      const title = hero.querySelector('.product-title');
      if (title?.tagName === 'H1') {
        const replacement = document.createElement('p');
        replacement.className = title.className;
        replacement.id = title.id;
        title.replaceWith(replacement);
      }
      put(hero, '.product-title', product.title);
      put(hero, '.product-subtitle', product.focus.title);
      put(hero, '.product-copy', product.benefit.intro);
      put(document.querySelector('.anchor-bar'), '.anchor-title', product.title);
      const microBenefits = hero.querySelector('.micro-benefits');
      if (microBenefits) microBenefits.hidden = !product.benefit.cards.length;
      hero.querySelectorAll('.micro-benefits span').forEach((item, i) => {
        const card = product.benefit.cards[i];
        item.hidden = !card;
        if (card) item.textContent = card.title;
      });
      const badge = hero.querySelector('.media-tag');
      if (badge) badge.hidden = true; // Editorial badges belong to the product, not the shared template.
      const rating = hero.querySelector('.rating');
      if (rating) rating.hidden = true; // No shared star score for an unreviewed product.
    }
    const benefit = document.querySelector('.benefit-story');
    if (automatic(benefit)) {
      benefit.querySelectorAll('.benefit-story-source').forEach(hide);
      const heading = benefit.querySelector('.benefit-story-title');
      if (heading?.tagName !== 'H1') {
        const replacement = document.createElement('h1');
        replacement.className = heading.className;
        heading.replaceWith(replacement);
      }
      put(benefit, '.eyebrow', product.benefit.eyebrow);
      put(benefit, '.benefit-story-title', product.benefit.title);
      put(benefit, '.benefit-story-intro', product.benefit.intro);
      benefit.querySelectorAll('.benefit-story-card').forEach((card, i) => {
        const item = product.benefit.cards[i];
        card.hidden = !item;
        if (item) { put(card, 'h3', item.title); put(card, 'p', item.body); }
      });
      benefit.querySelectorAll('.benefit-story-frame').forEach(frame => setImage(frame, 'img', product.benefit.image, galleryImage(/aplica[cç][aã]o|modelo|benef[ií]cio/i)));
    }
    const focus = document.querySelector('.product-focus');
    if (automatic(focus)) {
      put(focus, '.eyebrow', product.focus.eyebrow);
      put(focus, '.display', product.focus.title);
      put(focus, '.lead', product.focus.intro);
      put(focus, '.product-focus-signature', product.title);
      const resultLabel = focus.querySelector('.product-focus-results > .eyebrow');
      if (resultLabel) resultLabel.textContent = product.focus.percentages.length ? 'Resultados reais' : product.focus.studyLabel;
      const results = product.focus.percentages.length ? product.focus.percentages : product.focus.studyItems.map((item, i) => ({ number: String(i + 1).padStart(2, '0'), body: item }));
      focus.querySelectorAll('.product-focus-result').forEach((card, i) => {
        card.hidden = !results[i];
        if (results[i]) { put(card, 'strong', results[i].number); put(card, 'p', results[i].body); }
      });
      const note = focus.querySelector('.product-focus-results-note');
      if (note) {
        const disclosure = product.focus.note;
        note.hidden = !disclosure;
        if (disclosure) note.textContent = disclosure;
      }
      if (!results.length) hide(focus.querySelector('.product-focus-results'));
      setImage(focus, '.product-focus-sculpture', product.focus.image, galleryImage(/textura|cenario|cen[aá]rio/i));
      setImage(focus, '.product-focus-packshot', null, mainImage);
    }
    const formula = document.querySelector('.formula-section');
    if (automatic(formula)) {
      put(formula, '.formula-header .eyebrow', product.ingredients.eyebrow);
      put(formula, '.formula-header .display', product.ingredients.title);
      put(formula, '.formula-header .lead', product.ingredients.intro);
      formula.querySelectorAll('.ingredient-card').forEach((card, i) => {
        const item = product.ingredients.items[i];
        card.hidden = !item;
        if (item) {
          put(card, '.ingredient-meta', item.meta);
          put(card, 'h3', item.title);
          put(card, ':scope > p:last-child', item.body);
          setImage(card, '.ingredient-media img', item.image, galleryImage(/ingredientes/i));
        }
      });
      formula.querySelectorAll('.accordion details').forEach((detail, i) => {
        const item = product.ingredients.details[i];
        detail.hidden = !item;
        if (item) { put(detail, 'summary', item.title); put(detail, '.detail-body', item.body); }
      });
    }
    const science = document.querySelector('.science');
    if (automatic(science)) {
      put(science, '.eyebrow', product.science.eyebrow);
      put(science, '.display', product.science.title);
      put(science, '.lead', product.science.lines[0] || '');
      const points = product.science.points.length ? product.science.points : product.science.lines.slice(1);
      science.querySelectorAll('.science-list > div').forEach((item, i) => {
        item.hidden = !points[i];
        if (!item.hidden) put(item, 'p', points[i]);
      });
      setImage(science, '.science-visual img', product.science.image, galleryImage(/cenario|cen[aá]rio|benef[ií]cio/i));
    }
    const ritual = document.querySelector('.ritual');
    if (automatic(ritual)) {
      put(ritual, '.eyebrow', product.ritual.eyebrow);
      put(ritual, '.display', product.ritual.title);
      put(ritual, '.ritual-intro', product.ritual.intro);
      ritual.querySelectorAll('.ritual-path li').forEach((item, i) => {
        item.hidden = !product.ritual.steps[i];
        if (!item.hidden) { put(item, 'strong', product.ritual.steps[i].title); put(item, 'p', product.ritual.steps[i].body); }
      });
      const note = ritual.querySelector('.ritual-note');
      if (note) note.hidden = true;
      setImage(ritual, '.ritual-image img', product.ritual.image, galleryImage(/modo-de-uso|aplica[cç][aã]o/i));
    }
    const testimonials = document.querySelector('.testimonial-feature');
    if (automatic(testimonials)) {
      if (!product.testimonials.reviews.length) hide(testimonials);
      else {
        put(testimonials, '.eyebrow', product.testimonials.eyebrow);
        put(testimonials, '.display', product.testimonials.title);
        put(testimonials, '.lead', product.testimonials.intro);
        testimonials.querySelectorAll('.testimonial-review').forEach((card, i) => {
          const item = product.testimonials.reviews[i];
          card.hidden = !item;
          if (item) { put(card, 'blockquote', item.quote); put(card, 'footer strong', item.name); put(card, 'footer span', item.meta); }
        });
        testimonials.querySelectorAll('.testimonial-main').forEach(main => setImage(main, 'img', product.testimonials.image, mainImage));
      }
    }
    const expectation = document.querySelector('.expectation');
    if (automatic(expectation)) {
      put(expectation, '.display', product.expectation.title);
      put(expectation, '.lead', product.expectation.intro);
      expectation.querySelectorAll('.expectation-phase').forEach((phase, i) => {
        const item = product.expectation.phases[i];
        phase.hidden = !item;
        if (item) { put(phase, 'h3', item.title); put(phase, 'p', item.body); }
      });
      const note = expectation.querySelector('.expectation-note');
      if (note) note.hidden = true;
    }
    const faq = document.querySelector('.faq-section');
    if (automatic(faq)) {
      put(faq, '.eyebrow', product.faq.eyebrow);
      put(faq, '.display', product.faq.title);
      put(faq, '.faq-intro', product.faq.intro);
      const list = faq.querySelector('.faq-list');
      if (list) list.replaceChildren(...product.faq.items.map((item, i) => {
        const detail = document.createElement('details');
        if (!i) detail.open = true;
        const summary = document.createElement('summary');
        const body = document.createElement('div');
        body.className = 'detail-body';
        summary.textContent = item.question;
        body.textContent = item.answer;
        detail.append(summary, body);
        return detail;
      }));
      if (!product.faq.items.length) hide(faq);
      faq.querySelectorAll('.faq-blog').forEach(hide); // A shared Honey Balm editorial link is not product-specific.
    }
    document.documentElement.dataset.mhpCopySynced = 'flexible';
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hydrate);
  else hydrate();
  document.addEventListener('shopify:section:load', hydrate);
})();

